import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import EMPButton from "../../../../shared/components/EMPButton/EMPButton";
import styles from "./RouteOneIdAccordion.module.scss";

const RouteOneIdAccordion = ({
  routeOneDealerIds,
  selectedRouteOneDealerId,
  savedRouteOneDealerId,
  onRouteOneDealerIdChange,
  onSave,
  isLoading,
  isSaving,
  hasError,
}) => {
  const { t } = useTranslation();

  const handleDropdownChange = (event) => {
    onRouteOneDealerIdChange(event.target.value);
  };

  const handleSave = () => {
    onSave();
  };

  const isSaveDisabled =
    !selectedRouteOneDealerId ||
    selectedRouteOneDealerId === savedRouteOneDealerId ||
    isLoading ||
    isSaving;

  let placeholderText = t(
    "DealerOnboarding.content.financing.routeOneId.dropdownPlaceholder",
    "Select"
  );

  if (hasError) {
    placeholderText = t(
      "DealerOnboarding.content.financing.routeOneId.loadErrorPlaceholder",
      "Unable to load"
    );
  } else if (isLoading) {
    placeholderText = t(
      "DealerOnboarding.content.financing.routeOneId.loading",
      "Loading…"
    );
  }

  return (
    <Box className={styles.container} data-testid="route-one-id-accordion">
      <Typography variant="body1" className={styles.copy}>
        {t(
          "DealerOnboarding.content.financing.routeOneId.copy",
          "Confirm this is the ID your dealership will use within RouteOne. You can elect to utilize the same RouteOne ID selected during your previous setup, or you can select a new ID from the dropdown. If you move forward with your existing ID, all of your dealership’s previous settings will prepopulate in RouteOne. You can make edits at any time."
        )}
      </Typography>

      <Box className={styles.formRow}>
        <Box className={styles.dropdownField}>
          <Typography
            component="label"
            htmlFor="route-one-dealer-id"
            className={styles.fieldLabel}
          >
            {t(
              "DealerOnboarding.content.financing.routeOneId.dropdownTitle",
              "RouteOne Dealer ID"
            )}
          </Typography>

          <FormControl className={styles.dropdownControl}>
            <Select
              id="route-one-dealer-id"
              value={selectedRouteOneDealerId}
              onChange={handleDropdownChange}
              displayEmpty
              disabled={isLoading || hasError || isSaving}
              className={styles.dropdownSelect}
              inputProps={{
                "data-testid": "route-one-dealer-id-dropdown",
                "aria-label": t(
                  "DealerOnboarding.content.financing.routeOneId.dropdownTitle",
                  "RouteOne Dealer ID"
                ),
              }}
            >
              <MenuItem value="">
                <span className={styles.placeholderText}>
                  {placeholderText}
                </span>
              </MenuItem>

              {routeOneDealerIds.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <EMPButton
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
          testId="route-one-dealer-id-save-button"
          className={styles.saveButton}
        >
          {t("DealerOnboarding.content.financing.routeOneId.save", "Save")}
        </EMPButton>
      </Box>

      <Box className={styles.subtextRow}>
        <span className={styles.infoIcon} aria-hidden="true">
          i
        </span>
        <Typography variant="body2" className={styles.subtext}>
          {t(
            "DealerOnboarding.content.financing.routeOneId.subtext",
            "You must select a RouteOne Dealer ID before you complete Protection Plans in the next section."
          )}
        </Typography>
      </Box>
    </Box>
  );
};

RouteOneIdAccordion.propTypes = {
  routeOneDealerIds: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedRouteOneDealerId: PropTypes.string.isRequired,
  savedRouteOneDealerId: PropTypes.string,
  onRouteOneDealerIdChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  hasError: PropTypes.bool,
};

RouteOneIdAccordion.defaultProps = {
  savedRouteOneDealerId: "",
  isLoading: false,
  isSaving: false,
  hasError: false,
};

export default RouteOneIdAccordion;
